import { gql, QueryResult, useQuery } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import Modal from '../../../../common/src/components/Modal'
import {
  IntroText,
  Spacer,
  H3,
  AlignRight,
  BodyMonoText,
  SpaceBetweenFlexbox,
  SmallerTextSpan,
} from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { TestIds } from '../../../../common/src/data/testids'
import { nPlural } from '../../../../common/src/format'
import {
  BotActionValidationType,
  FetchActionQuery,
  FetchActionQueryVariables,
  FetchThreadQuery,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { retryAction } from '../../graphql/mutations'

interface ResendActionModalProps {
  onClose: () => void
  thread: FetchThreadQuery['thread']
  message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  validationErrors: FetchThreadQuery['thread']['recentActions'][0]['validationErrors']
  user: FetchUserContextQuery['self']
}

interface ResendActionModalState {
  resending: boolean
}

export const fetchActionQuery = gql`
  query FetchAction($savedActionId: Int!) {
    action(savedActionId: $savedActionId) {
      commandTypes
      commandPreviews
    }
  }
`

class ResendActionModalImpl extends React.Component<
  ResendActionModalProps & QueryResult<FetchActionQuery, FetchActionQueryVariables>,
  ResendActionModalState
> {
  state = {
    resending: false,
  }

  public render() {
    const { onClose, validationErrors, thread } = this.props

    // if there's a validation error that shouldn't allow re-running, show that
    let validationError = validationErrors.find(e => !e.canBeRerunInCC)
    if (validationError === undefined) {
      validationError = validationErrors[0]
    }

    return (
      <Modal title={this.resendingModalTitle(validationError.validationType)} width={500} onClose={onClose}>
        <IntroText>{validationError.errorDescription}</IntroText>
        <Spacer height={64} />
        <H3>Who can fix this?</H3>
        <Spacer height={8} />
        <IntroText>{this.whoCanFixThisString(validationError, thread.isUnsubscribed)}</IntroText>
        <Spacer height={64} />
        <H3>What happens when you retry?</H3>
        <Spacer height={8} />
        {this.listActions()}
        <Spacer height={32} />
        <AlignRight>{this.resendButton(validationError, thread.isUnsubscribed)}</AlignRight>
      </Modal>
    )
  }

  private resendButton(
    validationError: FetchThreadQuery['thread']['recentActions'][0]['validationErrors'][0],
    isUnsubscribed: boolean
  ) {
    const { data } = this.props
    const { resending } = this.state
    if (data?.action) {
      const numCommands = nPlural(data.action.commandTypes.length, 'command')
      return (
        <Button
          disabled={resending || !this.userCanRerun(validationError, isUnsubscribed)}
          onClick={this.onResendClick}
          data-testid={TestIds.retryActionButton}
          size="medium"
        >
          Retry action with {numCommands}
        </Button>
      )
    } else {
      return (
        <Button disabled={true} size="medium">
          Retry action
        </Button>
      )
    }
  }

  private onResendClick = () => {
    const { onClose, thread, message } = this.props
    if (!message.actionId) {
      return
    }
    this.setState({ resending: true })

    retryAction({ threadId: thread.id, actionId: message.actionId })
      .then(() => {
        onClose()
        this.setState({ resending: false })
      })
      .catch(handleError)
  }

  private userCanRerun(
    validationError: FetchThreadQuery['thread']['recentActions'][0]['validationErrors'][0],
    isUnsubscribed: boolean
  ) {
    const { user } = this.props

    switch (validationError.validationType) {
      case BotActionValidationType.MESSAGE_BODY_VALIDATION:
        return user.permissions.canResendInvalidBodyViolations
      case BotActionValidationType.THREAD_ACTIVITY_VALIDATION:
        return user.permissions.canResendTcpaViolations
      case BotActionValidationType.MASS_CANCELLATION_VALIDATION:
        return user.permissions.canResendMassCancellationViolations
      case BotActionValidationType.ALLOWED_ACTION_VALIDATION:
        return user.permissions.canResendAllowedActionViolations
      case BotActionValidationType.UNSUBSCRIBED_THREAD_VALIDATION:
        return !isUnsubscribed
      case BotActionValidationType.TEST_VALIDATION:
        return true
    }
  }

  private whoCanFixThisString(
    validationError: FetchThreadQuery['thread']['recentActions'][0]['validationErrors'][0],
    isUnsubscribed: boolean
  ) {
    if (this.userCanRerun(validationError, isUnsubscribed)) {
      return 'You can fix this by retrying the action.'
    }
    switch (validationError.validationType) {
      case BotActionValidationType.UNSUBSCRIBED_THREAD_VALIDATION:
        return 'The prospect needs to send us a message to resubscribe.'
      case BotActionValidationType.MASS_CANCELLATION_VALIDATION:
        return 'Ask an engineer or team lead.'
      default:
        return 'A Senior Operator, Operations Manager, Support, or Engineer can retry this action.'
    }
  }

  private resendingModalTitle(validationType: BotActionValidationType) {
    switch (validationType) {
      case BotActionValidationType.MESSAGE_BODY_VALIDATION:
        return 'Message check failure'
      case BotActionValidationType.THREAD_ACTIVITY_VALIDATION:
        return 'TCPA validation failure'
      case BotActionValidationType.MASS_CANCELLATION_VALIDATION:
        return 'Mass cancellation validation failure'
      case BotActionValidationType.ALLOWED_ACTION_VALIDATION:
        return 'Action not allowed'
      case BotActionValidationType.UNSUBSCRIBED_THREAD_VALIDATION:
        return 'Unsubscribed thread validation failure'
      case BotActionValidationType.TEST_VALIDATION:
        return 'Test validation failure'
    }
  }

  private listActions() {
    const { data } = this.props

    if (data?.action) {
      const ret = []
      for (let i = 0; i < data.action.commandTypes.length; i++) {
        // if there is a preview for this commandtype, only show the first 40 characters
        let previewText = ''
        let previewTitle = ''
        if (data.action.commandPreviews[i] != null) {
          if (data.action.commandPreviews[i].length > 30) {
            previewText = data.action.commandPreviews[i].substring(0, 30) + '...'
            previewTitle = data.action.commandPreviews[i]
          } else {
            previewText = data.action.commandPreviews[i]
          }
        }
        ret.push(
          <SpaceBetweenFlexbox key={i}>
            <BodyMonoText color="white">{data.action.commandTypes[i]}</BodyMonoText>
            <SmallerTextSpan title={previewTitle}>{previewText}</SmallerTextSpan>
          </SpaceBetweenFlexbox>
        )
      }
      return ret
    } else {
      return null
    }
  }
}

export default function ResendActionModal(props: ResendActionModalProps) {
  const { actionId } = props.message
  if (actionId == null) {
    return null
  }
  const res = useQuery<FetchActionQuery, FetchActionQueryVariables>(fetchActionQuery, {
    variables: {
      savedActionId: actionId,
    },
  })

  return <ResendActionModalImpl {...props} {...res} />
}
